// src/screen/Banner.js
// Professional, organized page banner — used across every inner page.
// Backwards-compatible with legacy `currentPage` prop.
import React from "react";
import { ChevronRight, Home } from "lucide-react";
import { useNavigate } from "react-router-dom";

const PageBanner = ({
  currentPage,
  title,
  subtitle,
  breadcrumb,
  icon,
  actions, // optional React node shown on the right
}) => {
  const navigate = useNavigate();

  const resolvedTitle = title || currentPage || "";
  const crumbs =
    breadcrumb && breadcrumb.length > 0
      ? breadcrumb
      : currentPage
      ? [{ label: currentPage }]
      : [];

  return (
    <section className="relative bg-gradient-to-r from-brand-700 to-brand-600 pt-32 pb-12 border-b-4 border-brand-800/30">
      {/* Subtle pattern overlay */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.07]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
          backgroundSize: "22px 22px",
        }}
      />
      {/* Soft accent highlight on the right */}
      <div
        aria-hidden="true"
        className="absolute right-0 top-0 bottom-0 w-1/3 bg-gradient-to-l from-white/5 to-transparent"
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        {crumbs.length > 0 && (
          <nav
            aria-label="Fil d'Ariane"
            className="flex items-center gap-1.5 text-xs font-medium text-white/70 mb-4"
          >
            <button
              onClick={() => navigate("/")}
              className="flex items-center gap-1 hover:text-white transition-colors"
            >
              <Home size={12} /> Accueil
            </button>
            {crumbs.map((b, i) => (
              <span key={i} className="flex items-center gap-1.5">
                <ChevronRight size={12} className="text-white/40" />
                {b.path ? (
                  <button
                    onClick={() => navigate(b.path)}
                    className="hover:text-white transition-colors"
                  >
                    {b.label}
                  </button>
                ) : (
                  <span className="text-white">{b.label}</span>
                )}
              </span>
            ))}
          </nav>
        )}

        {/* Title row */}
        <div className="flex items-center justify-between gap-6 flex-wrap">
          <div className="flex items-center gap-4 min-w-0">
            {icon && (
              <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-white/10 ring-1 ring-white/20 text-white flex items-center justify-center flex-shrink-0">
                {icon}
              </div>
            )}
            <div className="min-w-0">
              {resolvedTitle && (
                <h1 className="text-2xl md:text-3xl lg:text-[34px] font-black text-white leading-tight tracking-tight truncate">
                  {resolvedTitle}
                </h1>
              )}
              {subtitle && (
                <p className="mt-1.5 text-white/80 text-sm md:text-[15px] max-w-2xl">
                  {subtitle}
                </p>
              )}
            </div>
          </div>

          {actions && (
            <div className="flex items-center gap-2 flex-shrink-0">
              {actions}
            </div>
          )}
        </div>

        {/* Accent underline */}
        <div className="mt-6 h-[3px] w-16 bg-white/70 rounded-full" />
      </div>
    </section>
  );
};

export default PageBanner;
